import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type {
  EmptyStateInsightsPromptProps,
  InvestigationView,
} from '@/components/investigations/EmptyStateInsightsPrompt'
import type { AiInsight, Lens } from '@/features/ai-insights'
import { useAiInsights } from '@/features/ai-insights/hooks/useAiInsights'
import { useHypotheses } from '@/features/hypotheses'
import { useCaseContext } from '@/pages/cases/CaseContext'
import { graphApi } from '@/services/api/graph'
import {
  useInvestigationsStore,
  useInvestigationsStoreApi,
} from '@/stores/investigations-store-provider'
import { useObjectSetContributionsStore } from '@/stores/object-set-contributions-store'
import { emptyViewState } from '@/types/graph-view'

const VIEW_TO_LENS: Record<InvestigationView, Lens> = {
  graph: 'graph',
  map: 'geo_static',
  timeline: 'timeline',
}

/** Lenses the AI-insights backend actually supports today. Geo / timeline
 *  return `lens 'geo_static' not supported` so we skip the hook entirely
 *  for those views and present an empty insights list. */
const AI_SUPPORTED_VIEWS: ReadonlySet<InvestigationView> = new Set(['graph'])

const MAX_INVESTIGATIONS = 3
const MAX_RECENT_SEARCHES = 5

/**
 * Builds the full prop bundle for `<EmptyStateInsightsPrompt />`.
 *
 * The cold-start branches (no entities / no documents) are decided inside the
 * component based on `caseEntityCount` and `caseDocumentCount`, so callers
 * just spread the result and pass `view`.
 */
export function useInvestigateEmptyStateProps(
  view: InvestigationView
): Omit<EmptyStateInsightsPromptProps, 'view'> {
  const { caseId, caseData } = useCaseContext()
  const navigate = useNavigate()

  const storeApi = useInvestigationsStoreApi()
  const saved = useInvestigationsStore(s => s.saved)
  // Only ask the AI-insights hook to do anything for views the backend
  // actually supports today. For map / timeline, pass null so the hook
  // bails out and returns empty state — no list / runs network calls.
  const aiSupported = AI_SUPPORTED_VIEWS.has(view)
  const aiInsights = useAiInsights(aiSupported ? caseId : null, VIEW_TO_LENS[view])
  const { data: hypotheses } = useHypotheses(caseId)

  const openInvestigations = useMemo(
    () =>
      saved.slice(0, MAX_INVESTIGATIONS).map(inv => ({
        id: inv.id,
        title: inv.title,
        nodeCount: inv.graphView?.nodes?.length,
        onPick: () => storeApi.getState().setActive(inv.id),
      })),
    [saved, storeApi]
  )

  const recentSearches = useMemo(
    () =>
      (hypotheses ?? []).slice(0, MAX_RECENT_SEARCHES).map(h => ({
        id: h.id,
        title: h.title,
        onPick: () => navigate(`/cases/${caseId}/search/${h.id}`),
      })),
    [hypotheses, navigate, caseId]
  )

  const handleOpenInsight = async (insight: AiInsight) => {
    if (insight.entities.length === 0) return
    // Render the full set the agent meant for the graph, not just the
    // named entities. `evidence.focus_node_ids` typically includes
    // bridging intermediates that are needed for the user to see *why*
    // the named entities are connected — without them the graph view
    // shows isolated nodes. Union with entity ids in case the agent
    // omitted them from focus_node_ids by mistake.
    const entityNameById = new Map(insight.entities.map(e => [e.neo4j_id, e]))
    const idsToHydrate = Array.from(
      new Set([...insight.evidence.focus_node_ids, ...insight.entities.map(e => e.neo4j_id)])
    )
    const hydrated = await Promise.all(
      idsToHydrate.map(async id => {
        const ref = entityNameById.get(id)
        try {
          const node = await graphApi.getNode(caseId, id)
          return {
            id: node.id,
            labels: node.labels?.length ? node.labels : ref?.type ? [ref.type] : [],
            properties: node.properties ?? { name: ref?.name ?? id },
          }
        } catch {
          // Network or 404 — fall back to the agent's hint when we have
          // one, otherwise drop the node so the graph doesn't render an
          // unlabeled placeholder for an intermediate we couldn't fetch.
          if (!ref) return null
          return {
            id,
            labels: ref.type ? [ref.type] : [],
            properties: { name: ref.name, type: ref.type },
          }
        }
      })
    ).then(list => list.filter((n): n is NonNullable<typeof n> => n !== null))
    const view = {
      meta: {
        source: 'assistant' as const,
        createdAt: new Date().toISOString(),
        caseId,
        label: insight.headline,
      },
      nodes: hydrated,
      edges: [],
      view: emptyViewState(),
    }
    const result = storeApi.getState().addToObjectSet(view, insight.headline, 'insight')
    if (result.addedNodeIds.length > 0 || result.addedEdgeIds.length > 0) {
      const state = storeApi.getState()
      const investigationKey = state.activeId ?? (state.transient ? 'transient' : null)
      if (investigationKey) {
        useObjectSetContributionsStore.getState().add({
          id: result.contributionId,
          investigationKey,
          label: insight.headline,
          source: 'insight',
          nodeIds: result.addedNodeIds,
          edgeIds: result.addedEdgeIds,
        })
      }
    }
    void aiInsights.accept(insight.id)
  }

  return {
    caseEntityCount: caseData?.entity_count,
    caseRelationshipCount: caseData?.relationship_count,
    caseDocumentCount: caseData?.document_count,
    openInvestigations,
    aiInsights: aiInsights.insights,
    aiInsightsLoading: aiInsights.refreshing,
    recentSearches,
    onOpenInsight: handleOpenInsight,
    onSkipInsight: id => void aiInsights.skip(id),
    onGenerateInsights: () => void aiInsights.refresh(),
    onAskAgent: () => navigate(`/cases/${caseId}/search`),
    onOpenDocuments: () => navigate(`/cases/${caseId}/documents`),
    onOpenExtractions: () => navigate(`/cases/${caseId}/documents`),
  }
}
